import React, { useState, useRef, useEffect } from 'react';
import { API_BASE_URL, API_URL } from "../config";
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  FaUpload, FaTimes, FaPlus, FaTrash, FaCheckCircle, 
  FaBuilding, FaUser, FaEnvelope, FaPhone, FaMapMarkerAlt, 
  FaUniversity, FaFileAlt, FaDollarSign, FaCalendarAlt, FaBriefcase,
  FaEye, FaEyeSlash, FaSearch
} from 'react-icons/fa';

// Country codes list
const countryCodes = [
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'United Kingdom', flag: '🇬🇧' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
  { code: '+46', country: 'Sweden', flag: '🇸🇪' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
  { code: '+234', country: 'Nigeria', flag: '🇳🇬' },
  { code: '+254', country: 'Kenya', flag: '🇰🇪' },
  { code: '+233', country: 'Ghana', flag: '🇬🇭' },
  { code: '+260', country: 'Zambia', flag: '🇿🇲' },
  { code: '+263', country: 'Zimbabwe', flag: '🇿🇼' },
  { code: '+267', country: 'Botswana', flag: '🇧🇼' },
  { code: '+264', country: 'Namibia', flag: '🇳🇦' },
  { code: '+258', country: 'Mozambique', flag: '🇲🇿' },
  { code: '+266', country: 'Lesotho', flag: '🇱🇸' },
  { code: '+268', country: 'Eswatini', flag: '🇸🇿' },
];

function ClientSignup() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Country code state
  const [selectedCountryCode, setSelectedCountryCode] = useState('+27');
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const countryDropdownRef = useRef(null);

  // Password strength state
  const [passwordStrength, setPasswordStrength] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false
  });
  const [passwordScore, setPasswordScore] = useState(0);

  const [formData, setFormData] = useState({
    // Company Details
    company_name: '',
    trading_name: '',
    registration_number: '',
    vat_number: '',
    company_type: 'Pty Ltd',
    
    // Contact Details
    contact_person: '',
    email: '',
    phone: '',
    mobile: '',
    address: '',
    city: '',
    province: '',
    postal_code: '',
    
    // Client Type
    client_type: 'prepaid',
    
    // Banking Details
    bank_name: '',
    account_holder: '',
    account_number: '',
    branch_code: '',
    account_type: 'Cheque',
    
    // Credit Specific
    requested_credit_limit: '',
    requested_payment_terms: '30 Days',
    billing_contact_person: '',
    billing_email: '',
    billing_phone: '',
    
    // Password - EMPTY by default
    password: '',
    confirm_password: '',
    
    // Terms
    accept_terms: false
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (countryDropdownRef.current && !countryDropdownRef.current.contains(event.target)) {
        setShowCountryDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCountries = countryCodes.filter(c => 
    c.country.toLowerCase().includes(countrySearch.toLowerCase()) ||
    c.code.includes(countrySearch)
  );

  const [references, setReferences] = useState([
    { company_name: '', contact_person: '', phone: '', email: '', relationship: '' }
  ]);

  const [files, setFiles] = useState({
    // ALL CLIENTS - MANDATORY
    proof_of_account: null,
    cipc_document: null,
    director_id: null,
    proof_of_address: null,
    // ALL CLIENTS - OPTIONAL
    vat_cert: null,
    bbbee_cert: null,
    // CREDIT CLIENTS ONLY
    bank_statements: null,
    registration_cert: null,
  });

  const [filePreviews, setFilePreviews] = useState({
    proof_of_account: null,
    cipc_document: null,
    director_id: null,
    proof_of_address: null,
    vat_cert: null,
    bbbee_cert: null,
    bank_statements: null,
    registration_cert: null,
  });

  const fileInputRefs = {
    proof_of_account: useRef(null),
    cipc_document: useRef(null),
    director_id: useRef(null),
    proof_of_address: useRef(null),
    vat_cert: useRef(null),
    bbbee_cert: useRef(null),
    bank_statements: useRef(null),
    registration_cert: useRef(null),
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, phone: value }));
  };

  const handleMobileChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    setFormData(prev => ({ ...prev, mobile: value }));
  };

  const handleReferenceChange = (index, field, value) => {
    const updated = [...references];
    updated[index][field] = value;
    setReferences(updated);
  };

  const addReference = () => {
    setReferences([
      ...references,
      { company_name: '', contact_person: '', phone: '', email: '', relationship: '' }
    ]);
  };

  const removeReference = (index) => {
    if (references.length <= 1) {
      toast.warning('You need at least one reference');
      return;
    }
    const updated = references.filter((_, i) => i !== index);
    setReferences(updated);
  };

  const checkPasswordStrength = (password) => {
    const strength = {
      minLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /[0-9]/.test(password),
      hasSpecial: /[@$!%*?&#]/.test(password)
    };
    setPasswordStrength(strength);
    const score = Object.values(strength).filter(Boolean).length;
    setPasswordScore(score);
    return strength;
  };

  const handlePasswordChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, password: value }));
    checkPasswordStrength(value);
  };

  const getStrengthColor = () => {
    if (passwordScore <= 2) return 'bg-red-500';
    if (passwordScore <= 3) return 'bg-yellow-500';
    if (passwordScore <= 4) return 'bg-blue-500';
    return 'bg-green-500';
  };

  const getStrengthText = () => {
    if (passwordScore <= 2) return 'Weak';
    if (passwordScore <= 3) return 'Fair';
    if (passwordScore <= 4) return 'Good';
    return 'Strong';
  };

  const getApiUrl = () => {
    if (process.env.NODE_ENV === 'production') {
      return 'https://onepurposelogistics-api.onrender.com/api';
    }
    return 'API_URL';
  };

  const handleFileChange = (e, key) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a PDF, JPEG, or PNG file');
      return;
    }

    setFiles(prev => ({ ...prev, [key]: file }));
    
    const reader = new FileReader();
    reader.onload = (e) => {
      setFilePreviews(prev => ({ ...prev, [key]: e.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const removeFile = (key) => {
    setFiles(prev => ({ ...prev, [key]: null }));
    setFilePreviews(prev => ({ ...prev, [key]: null }));
    if (fileInputRefs[key].current) {
      fileInputRefs[key].current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.company_name) {
      toast.error('Company Name is required');
      return;
    }
    if (!formData.contact_person) {
      toast.error('Contact Person is required');
      return;
    }
    if (!formData.email) {
      toast.error('Email is required');
      return;
    }
    if (!formData.phone) {
      toast.error('Phone is required');
      return;
    }
    if (!formData.password || formData.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    if (formData.password !== formData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    
    // ALL CLIENTS - MANDATORY DOCUMENTS
    if (!files.proof_of_account) {
      toast.error('Proof of Account (Bank Confirmation Letter) is required');
      return;
    }
    if (!files.cipc_document) {
      toast.error('CIPC Registration Document is required');
      return;
    }
    if (!files.director_id) {
      toast.error('Director ID/Passport is required');
      return;
    }
    if (!files.proof_of_address) {
      toast.error('Proof of Address is required');
      return;
    }
    
    if (!acceptedTerms) {
      toast.error('Please accept the Terms and Conditions');
      return;
    }

    // Credit client validations
    if (formData.client_type === 'credit') {
      if (!formData.requested_credit_limit) {
        toast.error('Requested Credit Limit is required for credit clients');
        return;
      }
      if (!files.bank_statements) {
        toast.error('6 Months Bank Statements are required for credit clients');
        return;
      }
      const hasValidReference = references.some(r => r.company_name && r.contact_person && r.phone);
      if (!hasValidReference) {
        toast.error('Please provide at least one trade reference');
        return;
      }
    }

    setLoading(true);
    const API_URL = getApiUrl();

    try {
      const uploadedFiles = {};
      
      const uploadFile = async (file, documentType) => {
        if (!file) return null;
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', documentType);
        
        const response = await fetch(`${API_URL}/client-documents/upload`, {
          method: 'POST',
          body: formData
        });
        const data = await response.json();
        return data.success ? data.url : null;
      };

      // All clients - mandatory
      uploadedFiles.proof_of_account = await uploadFile(files.proof_of_account, 'proof_of_account');
      uploadedFiles.cipc_document = await uploadFile(files.cipc_document, 'cipc');
      uploadedFiles.director_id = await uploadFile(files.director_id, 'director_id');
      uploadedFiles.proof_of_address = await uploadFile(files.proof_of_address, 'proof_of_address');
      
      // All clients - optional
      uploadedFiles.vat_cert = files.vat_cert ? await uploadFile(files.vat_cert, 'vat') : null;
      uploadedFiles.bbbee_cert = files.bbbee_cert ? await uploadFile(files.bbbee_cert, 'bbbee') : null;
      
      // Credit clients only
      if (formData.client_type === 'credit') {
        uploadedFiles.bank_statements = await uploadFile(files.bank_statements, 'bank_statements');
        uploadedFiles.registration_cert = files.registration_cert ? await uploadFile(files.registration_cert, 'registration') : null;
      }

      const payload = {
        ...formData,
        references: references.filter(r => r.company_name || r.contact_person || r.phone),
        documents: uploadedFiles
      };

      const response = await fetch(`${API_URL}/clients/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        toast.success('✅ Registration submitted successfully! Our team will review your application.');
        setTimeout(() => navigate('/login'), 3000);
      } else {
        toast.error(data.error || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      toast.error('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const isCredit = formData.client_type === 'credit';

  // Get selected country display
  const selectedCountry = countryCodes.find(c => c.code === selectedCountryCode);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-xl p-8">
        {/* Header - Updated with OneView Logo */}
        <div className="text-center mb-8">
          <div className="w-48 h-48 mx-auto mb-4">
            <img 
              src="/Images/OneViewLogo clear background.png" 
              alt="OneView" 
              className="w-full h-full object-contain"
            />
          </div>
          <h1 className="text-2xl font-bold">
          Become a <span style={{ color: '#00A86B' }}>One Purpose</span> Client
          </h1> 
          <p className="text-gray-500 mt-1">Join our network of trusted clients and get real-time logistics intelligence</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          
          {/* CLIENT TYPE SELECTOR */}
          <div className="bg-gray-50 rounded-xl p-4 border-2 border-gray-200">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Account Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, client_type: 'prepaid' }))}
                className={`p-4 rounded-xl border-2 transition-all text-center ${
                  formData.client_type === 'prepaid'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <span className="text-2xl block mb-1">💳</span>
                <span className="font-medium text-navy">Prepaid</span>
                <p className="text-xs text-gray-500 mt-1">Pay upfront</p>
              </button>
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, client_type: 'credit' }))}
                className={`p-4 rounded-xl border-2 transition-all text-center ${
                  formData.client_type === 'credit'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <span className="text-2xl block mb-1">📊</span>
                <span className="font-medium text-navy">Credit</span>
                <p className="text-xs text-gray-500 mt-1">Pay after service</p>
              </button>
            </div>
          </div>

          {/* COMPANY DETAILS */}
          <div className="border rounded-xl p-6">
            <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
              <FaBuilding className="text-green" /> Company Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  placeholder="e.g. ABC Logistics (Pty) Ltd"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trading Name
                </label>
                <input
                  type="text"
                  name="trading_name"
                  value={formData.trading_name}
                  onChange={handleChange}
                  placeholder="e.g. ABC Transport"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Registration Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="registration_number"
                  value={formData.registration_number}
                  onChange={handleChange}
                  required
                  placeholder="e.g. 2020/123456/07"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  VAT Number (Optional)
                </label>
                <input
                  type="text"
                  name="vat_number"
                  value={formData.vat_number}
                  onChange={handleChange}
                  placeholder="e.g. 4123456789"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="company_type"
                  value={formData.company_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent"
                >
                  <option value="Pty Ltd">Pty Ltd</option>
                  <option value="Sole Trader">Sole Trader</option>
                  <option value="Partnership">Partnership</option>
                  <option value="CC">CC</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* CONTACT DETAILS */}
          <div className="border rounded-xl p-6">
            <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
              <FaUser className="text-green" /> Contact Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Person <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="contact_person"
                  value={formData.contact_person}
                  onChange={handleChange}
                  required
                  placeholder="e.g. John Doe"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="e.g. john@abclogistics.co.za"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number <span className="text-red-500">*</span>
                </label>
                <div className="flex">
                  {/* Country Code Dropdown */}
                  <div className="relative" ref={countryDropdownRef}>
                    <button
                      type="button"
                      onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                      className="h-10 px-3 border border-r-0 border-gray-300 rounded-l-lg bg-gray-50 hover:bg-gray-100 flex items-center gap-1 text-sm"
                    >
                      {selectedCountry ? `${selectedCountry.flag} ${selectedCountry.code}` : '+27'}
                      <span className="ml-1">▼</span>
                    </button>
                    {showCountryDropdown && (
                      <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                        <div className="p-2 sticky top-0 bg-white border-b">
                          <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
                            <input
                              type="text"
                              placeholder="Search country..."
                              value={countrySearch}
                              onChange={(e) => setCountrySearch(e.target.value)}
                              className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-green"
                            />
                          </div>
                        </div>
                        {filteredCountries.map((c) => (
                          <button
                            key={c.code}
                            type="button"
                            onClick={() => {
                              setSelectedCountryCode(c.code);
                              setShowCountryDropdown(false);
                              setCountrySearch('');
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2 ${
                              selectedCountryCode === c.code ? 'bg-green-50' : ''
                            }`}
                          >
                            <span>{c.flag}</span>
                            <span>{c.code}</span>
                            <span className="text-gray-500">{c.country}</span>
                            {selectedCountryCode === c.code && <FaCheckCircle className="ml-auto text-green" size={14} />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    required
                    placeholder="e.g. 821234567"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number
                </label>
                <div className="flex">
                  <div className="h-10 px-3 border border-r-0 border-gray-300 rounded-l-lg bg-gray-50 flex items-center text-sm">
                    {selectedCountry ? selectedCountry.code : '+27'}
                  </div>
                  <input
                    type="tel"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleMobileChange}
                    placeholder="e.g. 821234568"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* ADDRESS */}
          <div className="border rounded-xl p-6">
            <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
              <FaMapMarkerAlt className="text-green" /> Physical Address <span className="text-red-500">*</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <input
                  type="text"
                  name="address"
                  placeholder="e.g. 123 Main Street, Sandton"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                />
              </div>
              <div>
                <input
                  type="text"
                  name="city"
                  placeholder="e.g. Johannesburg"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                />
              </div>
              <div>
                <input
                  type="text"
                  name="province"
                  placeholder="e.g. Gauteng"
                  value={formData.province}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                />
              </div>
              <div>
                <input
                  type="text"
                  name="postal_code"
                  placeholder="e.g. 2000"
                  value={formData.postal_code}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                />
              </div>
            </div>
          </div>

          {/* BANKING DETAILS */}
          <div className="border rounded-xl p-6">
            <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
              <FaUniversity className="text-green" /> Banking Details
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Bank Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="bank_name"
                  value={formData.bank_name}
                  onChange={handleChange}
                  required
                  placeholder="e.g. Standard Bank"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Holder <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="account_holder"
                  value={formData.account_holder}
                  onChange={handleChange}
                  required
                  placeholder="e.g. ABC Logistics (Pty) Ltd"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="account_number"
                  value={formData.account_number}
                  onChange={handleChange}
                  required
                  placeholder="e.g. 123456789"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Branch Code <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="branch_code"
                  value={formData.branch_code}
                  onChange={handleChange}
                  required
                  placeholder="e.g. 051001"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Account Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="account_type"
                  value={formData.account_type}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent"
                >
                  <option value="Cheque">Cheque</option>
                  <option value="Savings">Savings</option>
                  <option value="Transmission">Transmission</option>
                </select>
              </div>
            </div>
          </div>

          {/* MANDATORY DOCUMENTS FOR ALL CLIENTS */}
          <div className="border rounded-xl p-6 border-red-200 bg-red-50/30">
            <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
              <FaFileAlt className="text-red-500" /> Mandatory Documents <span className="text-red-500">*</span>
            </h2>
            <p className="text-sm text-gray-500 mb-4">These documents are required for ALL clients</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Proof of Account */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  📄 Proof of Account (Bank Confirmation Letter) <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-green-500 transition-colors">
                  <input
                    ref={fileInputRefs.proof_of_account}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'proof_of_account')}
                    className="hidden"
                  />
                  {filePreviews.proof_of_account ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-gray-600 truncate">{files.proof_of_account?.name}</span>
                      <button type="button" onClick={() => removeFile('proof_of_account')} className="text-red-500 hover:text-red-700">
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => fileInputRefs.proof_of_account.current?.click()} className="text-gray-500 hover:text-gray-700">
                      <FaUpload className="mx-auto text-xl mb-1" />
                      <p className="text-xs">Upload bank confirmation letter</p>
                    </button>
                  )}
                </div>
              </div>

              {/* CIPC Document */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  📋 CIPC Registration Document <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-green-500 transition-colors">
                  <input
                    ref={fileInputRefs.cipc_document}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'cipc_document')}
                    className="hidden"
                  />
                  {filePreviews.cipc_document ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-gray-600 truncate">{files.cipc_document?.name}</span>
                      <button type="button" onClick={() => removeFile('cipc_document')} className="text-red-500 hover:text-red-700">
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => fileInputRefs.cipc_document.current?.click()} className="text-gray-500 hover:text-gray-700">
                      <FaUpload className="mx-auto text-xl mb-1" />
                      <p className="text-xs">Upload CIPC certificate</p>
                    </button>
                  )}
                </div>
              </div>

              {/* Director ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  🪪 Director ID/Passport <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-green-500 transition-colors">
                  <input
                    ref={fileInputRefs.director_id}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'director_id')}
                    className="hidden"
                  />
                  {filePreviews.director_id ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-gray-600 truncate">{files.director_id?.name}</span>
                      <button type="button" onClick={() => removeFile('director_id')} className="text-red-500 hover:text-red-700">
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => fileInputRefs.director_id.current?.click()} className="text-gray-500 hover:text-gray-700">
                      <FaUpload className="mx-auto text-xl mb-1" />
                      <p className="text-xs">Upload ID/Passport</p>
                    </button>
                  )}
                </div>
              </div>

              {/* Proof of Address */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  📍 Proof of Address <span className="text-red-500">*</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-green-500 transition-colors">
                  <input
                    ref={fileInputRefs.proof_of_address}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'proof_of_address')}
                    className="hidden"
                  />
                  {filePreviews.proof_of_address ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-gray-600 truncate">{files.proof_of_address?.name}</span>
                      <button type="button" onClick={() => removeFile('proof_of_address')} className="text-red-500 hover:text-red-700">
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => fileInputRefs.proof_of_address.current?.click()} className="text-gray-500 hover:text-gray-700">
                      <FaUpload className="mx-auto text-xl mb-1" />
                      <p className="text-xs">Upload proof of address</p>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ✅ NEW: OPTIONAL DOCUMENTS FOR ALL CLIENTS */}
          <div className="border rounded-xl p-6 border-blue-200 bg-blue-50/30">
            <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
              <FaFileAlt className="text-blue-500" /> Additional Documents (Optional)
            </h2>
            <p className="text-sm text-gray-500 mb-4">Upload these documents to strengthen your application</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* VAT Certificate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  📄 VAT Certificate (Optional)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-green-500 transition-colors">
                  <input
                    ref={fileInputRefs.vat_cert}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'vat_cert')}
                    className="hidden"
                  />
                  {filePreviews.vat_cert ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-gray-600 truncate">{files.vat_cert?.name}</span>
                      <button type="button" onClick={() => removeFile('vat_cert')} className="text-red-500 hover:text-red-700">
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => fileInputRefs.vat_cert.current?.click()} className="text-gray-500 hover:text-gray-700">
                      <FaUpload className="mx-auto text-xl mb-1" />
                      <p className="text-xs">Upload VAT certificate</p>
                    </button>
                  )}
                </div>
              </div>

              {/* BBBEE Certificate */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  📄 BBBEE Certificate (Optional)
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-green-500 transition-colors">
                  <input
                    ref={fileInputRefs.bbbee_cert}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileChange(e, 'bbbee_cert')}
                    className="hidden"
                  />
                  {filePreviews.bbbee_cert ? (
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm text-gray-600 truncate">{files.bbbee_cert?.name}</span>
                      <button type="button" onClick={() => removeFile('bbbee_cert')} className="text-red-500 hover:text-red-700">
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => fileInputRefs.bbbee_cert.current?.click()} className="text-gray-500 hover:text-gray-700">
                      <FaUpload className="mx-auto text-xl mb-1" />
                      <p className="text-xs">Upload BBBEE certificate</p>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* CREDIT CLIENT ONLY FIELDS */}
          {isCredit && (
            <>
              {/* CREDIT INFORMATION */}
              <div className="border rounded-xl p-6 border-green-200 bg-green-50/30">
                <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                  <FaDollarSign className="text-green" /> Credit Information
                </h2>
                <p className="text-sm text-gray-500 mb-4">Please provide your credit requirements for assessment</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Requested Credit Limit (R) <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="requested_credit_limit"
                      value={formData.requested_credit_limit}
                      onChange={handleChange}
                      required={isCredit}
                      placeholder="e.g. 100000"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Requested Payment Terms <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="requested_payment_terms"
                      value={formData.requested_payment_terms}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent"
                    >
                      <option value="Pay in Advance (Prepaid)">Pay in Advance (Prepaid)</option>
                      <option value="COD">COD (Cash on Delivery)</option>
                      <option value="7 Days">7 Days</option>
                      <option value="15 Days">15 Days</option>
                      <option value="30 Days">30 Days</option>
                      <option value="50% Booking/50% EPOD">50% Booking/50% EPOD</option>
                      <option value="50% Booking/50% Completion">50% Booking/50% Completion</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Contact Person
                    </label>
                    <input
                      type="text"
                      name="billing_contact_person"
                      value={formData.billing_contact_person}
                      onChange={handleChange}
                      placeholder="e.g. Jane Smith"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Email
                    </label>
                    <input
                      type="email"
                      name="billing_email"
                      value={formData.billing_email}
                      onChange={handleChange}
                      placeholder="e.g. billing@abclogistics.co.za"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Billing Phone
                    </label>
                    <div className="flex">
                      <div className="h-10 px-3 border border-r-0 border-gray-300 rounded-l-lg bg-gray-50 flex items-center text-sm">
                        {selectedCountry ? selectedCountry.code : '+27'}
                      </div>
                      <input
                        type="tel"
                        name="billing_phone"
                        value={formData.billing_phone}
                        onChange={handleChange}
                        placeholder="e.g. 821234569"
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-r-lg focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* TRADE REFERENCES */}
              <div className="border rounded-xl p-6">
                <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                  <FaBriefcase className="text-green" /> Trade References (Min 1 Required)
                </h2>
                <p className="text-sm text-gray-500 mb-4">
                  References must have been active trading relationships in the last 12 months
                </p>
                {references.map((ref, index) => (
                  <div key={index} className="border rounded-lg p-4 mb-3 bg-gray-50 relative">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Company Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. ABC Suppliers"
                          value={ref.company_name}
                          onChange={(e) => handleReferenceChange(index, 'company_name', e.target.value)}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Contact Person <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Bob Brown"
                          value={ref.contact_person}
                          onChange={(e) => handleReferenceChange(index, 'contact_person', e.target.value)}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Phone <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="tel"
                          placeholder="e.g. 0111234567"
                          value={ref.phone}
                          onChange={(e) => handleReferenceChange(index, 'phone', e.target.value)}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Email
                        </label>
                        <input
                          type="email"
                          placeholder="e.g. bob@abcsuppliers.co.za"
                          value={ref.email}
                          onChange={(e) => handleReferenceChange(index, 'email', e.target.value)}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Relationship <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          placeholder="e.g. Supplier, Customer, Partner"
                          value={ref.relationship}
                          onChange={(e) => handleReferenceChange(index, 'relationship', e.target.value)}
                          className="w-full px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green focus:border-transparent placeholder-gray-400"
                        />
                      </div>
                      <div className="flex items-end justify-end">
                        <button
                          type="button"
                          onClick={() => removeReference(index)}
                          className="text-red-500 hover:text-red-700 p-2"
                          title="Remove reference"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addReference}
                  className="flex items-center gap-2 text-green-600 hover:text-green-700 text-sm font-medium"
                >
                  <FaPlus /> Add Reference
                </button>
              </div>

              {/* CREDIT CLIENT ADDITIONAL DOCUMENTS - Bank Statements only */}
              <div className="border rounded-xl p-6 border-green-200 bg-green-50/30">
                <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
                  <FaFileAlt className="text-green" /> Additional Documents for Credit Assessment
                </h2>
                
                {/* 6 Months Bank Statements - REQUIRED */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    📊 6 Months Bank Statements <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-3 text-center hover:border-green-500 transition-colors">
                    <input
                      ref={fileInputRefs.bank_statements}
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(e, 'bank_statements')}
                      className="hidden"
                    />
                    {filePreviews.bank_statements ? (
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-gray-600 truncate">{files.bank_statements?.name}</span>
                        <button type="button" onClick={() => removeFile('bank_statements')} className="text-red-500 hover:text-red-700">
                          <FaTimes />
                        </button>
                      </div>
                    ) : (
                      <button type="button" onClick={() => fileInputRefs.bank_statements.current?.click()} className="text-gray-500 hover:text-gray-700">
                        <FaUpload className="mx-auto text-xl mb-1" />
                        <p className="text-xs">Upload 6 months bank statements</p>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* PASSWORD - With Strength Checker */}
          <div className="border rounded-xl p-6">
            <h2 className="text-lg font-semibold text-navy mb-4 flex items-center gap-2">
              <FaUser className="text-green" /> Account Password
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handlePasswordChange}
                    required
                    minLength="8"
                    placeholder="Create a strong password"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green focus:border-transparent pr-10 placeholder-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-1">Minimum 8 characters</p>
                
                {/* Password Strength Indicators */}
                {formData.password && (
                  <div className="mt-3 space-y-1.5">
                    <div className="flex justify-between items-center">
                      <span className="text-xs font-medium text-gray-600">Password Strength</span>
                      <span className={`text-xs font-bold ${passwordScore <= 2 ? 'text-red-500' : passwordScore <= 3 ? 'text-yellow-500' : passwordScore <= 4 ? 'text-blue-500' : 'text-green-500'}`}>
                        {getStrengthText()}
                      </span>
                    </div>
                    <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full transition-all duration-300 ${getStrengthColor()}`}
                        style={{ width: `${(passwordScore / 5) * 100}%` }}
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-xs">
                      <div className={`flex items-center gap-1 ${passwordStrength.minLength ? 'text-green-600' : 'text-gray-400'}`}>
                        {passwordStrength.minLength ? <FaCheckCircle size={12} /> : <FaTimes size={12} />} 8+ characters
                      </div>
                      <div className={`flex items-center gap-1 ${passwordStrength.hasUppercase ? 'text-green-600' : 'text-gray-400'}`}>
                        {passwordStrength.hasUppercase ? <FaCheckCircle size={12} /> : <FaTimes size={12} />} Uppercase
                      </div>
                      <div className={`flex items-center gap-1 ${passwordStrength.hasLowercase ? 'text-green-600' : 'text-gray-400'}`}>
                        {passwordStrength.hasLowercase ? <FaCheckCircle size={12} /> : <FaTimes size={12} />} Lowercase
                      </div>
                      <div className={`flex items-center gap-1 ${passwordStrength.hasNumber ? 'text-green-600' : 'text-gray-400'}`}>
                        {passwordStrength.hasNumber ? <FaCheckCircle size={12} /> : <FaTimes size={12} />} Number
                      </div>
                      <div className={`flex items-center gap-1 col-span-2 ${passwordStrength.hasSpecial ? 'text-green-600' : 'text-gray-400'}`}>
                        {passwordStrength.hasSpecial ? <FaCheckCircle size={12} /> : <FaTimes size={12} />} Special character (@$!%*?&#)
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirm_password"
                    value={formData.confirm_password}
                    onChange={handleChange}
                    required
                    placeholder="Confirm your password"
                    className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-green focus:border-transparent pr-10 placeholder-gray-400 ${
                      formData.confirm_password && formData.password === formData.confirm_password && formData.password
                        ? 'border-green-500 bg-green-50'
                        : formData.confirm_password && formData.password !== formData.confirm_password
                        ? 'border-red-500 bg-red-50'
                        : 'border-gray-300'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {formData.confirm_password && formData.password !== formData.confirm_password && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <FaTimes size={12} /> Passwords do not match
                  </p>
                )}
                {formData.confirm_password && formData.password === formData.confirm_password && formData.password && (
                  <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                    <FaCheckCircle size={12} /> Passwords match
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* TERMS & CONDITIONS */}
          <div className="border rounded-xl p-6">
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 w-5 h-5 rounded border-gray-300 text-green-600 focus:ring-green-500"
                required
              />
              <div>
                <label className="text-sm text-gray-700">
                  I have read and agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTerms(true)}
                    className="text-green-600 hover:underline font-medium"
                  >
                    Terms and Conditions
                  </button>
                  {' '}of One Purpose Logistics (Pty) Ltd.
                  <span className="text-red-500">*</span>
                </label>
              </div>
            </div>
          </div>

          {/* SUBMIT BUTTON */}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-navy text-white rounded-xl font-semibold hover:bg-green hover:text-navy transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <span className="animate-spin">🔄</span> Submitting...
              </>
            ) : (
              'Submit Application →'
            )}
          </button>

          <div className="text-center">
            <Link to="/login" className="text-sm text-green hover:underline">
              Already have an account? Login here
            </Link>
          </div>
        </form>
      </div>

      {/* TERMS & CONDITIONS MODAL */}
      {showTerms && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setShowTerms(false)}></div>
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-2xl flex justify-between items-center">
              <h2 className="text-xl font-bold text-navy">Terms and Conditions</h2>
              <button onClick={() => setShowTerms(false)} className="text-gray-500 hover:text-navy">
                <FaTimes size={24} />
              </button>
            </div>
            
            <div className="p-6 space-y-4 text-sm text-gray-700">
              <h3 className="text-lg font-bold text-navy">1. DEFINITIONS AND INTERPRETATION</h3>
              <p>In these Terms and Conditions, unless the context otherwise requires:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li><strong>"Client"</strong> means the person or entity registering for services with One Purpose Logistics (Pty) Ltd.</li>
                <li><strong>"Company"</strong> means One Purpose Logistics (Pty) Ltd, registration number 2023/124065/07.</li>
                <li><strong>"Services"</strong> means the logistics and transport services provided by the Company.</li>
                <li><strong>"Agreement"</strong> means these Terms and Conditions as accepted by the Client.</li>
                <li><strong>"NCR"</strong> means the National Credit Regulator of South Africa.</li>
              </ul>

              <h3 className="text-lg font-bold text-navy">2. APPLICATION AND ACCEPTANCE</h3>
              <p>2.1 These Terms and Conditions apply to all services provided by the Company to the Client and constitute a legally binding agreement.</p>
              <p>2.2 By submitting a registration application, the Client acknowledges that they have read, understood, and agree to be bound by these Terms and Conditions.</p>
              <p>2.3 The Client confirms that all information provided in the application is true, accurate, and complete to the best of their knowledge.</p>

              <h3 className="text-lg font-bold text-navy">3. CREDIT FACILITIES (For Credit Clients)</h3>
              <p>3.1 Credit facilities are granted at the sole discretion of the Company and are subject to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Approval by the Company's credit committee;</li>
                <li>Verification of all documentation provided;</li>
                <li>Credit reference checks;</li>
                <li>Compliance with the National Credit Act 34 of 2005 ("NCA").</li>
              </ul>
              <p>3.2 The Client acknowledges that credit facilities may be withdrawn at any time without notice should the Client:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Fail to make payment on the due date;</li>
                <li>Exceed their approved credit limit;</li>
                <li>Provide false or misleading information;</li>
                <li>Be in breach of any term of this Agreement.</li>
              </ul>
              <p>3.3 The Client has the right to:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Apply for credit at any time;</li>
                <li>Receive reasons for credit decline in writing;</li>
                <li>Request a credit limit review;</li>
                <li>Access their credit information held by the Company.</li>
              </ul>
              <p>3.4 The Company shall treat all credit applications in accordance with the NCA and POPIA.</p>

              <h3 className="text-lg font-bold text-navy">4. PAYMENT TERMS</h3>
              <p>4.1 Prepaid Clients: Payment must be made in full before services are rendered.</p>
              <p>4.2 Credit Clients: Payment is due within the approved payment terms (COD, 7, 15, or 30 days from invoice date).</p>
              <p>4.3 Interest of 2% per month (24% per annum) will be charged on all overdue accounts in accordance with the National Credit Act.</p>
              <p>4.4 The Company reserves the right to suspend services immediately should payment not be received on the due date.</p>
              <p>4.5 All payments must be made via EFT into the Company's designated bank account.</p>

              <h3 className="text-lg font-bold text-navy">5. INVOICING</h3>
              <p>5.1 Invoices will be issued upon delivery of services or as agreed.</p>
              <p>5.2 The cut-off date for receipt of original invoices with complete original documentation is the 25th of each month.</p>
              <p>5.3 Invoices received after the 25th will be carried over to the following month's payment cycle.</p>
              <p>5.4 Disputes must be logged in writing within 7 days of invoice date.</p>

              <h3 className="text-lg font-bold text-navy">6. DOCUMENTATION</h3>
              <p>6.1 The Client warrants that all documentation submitted is authentic, accurate, and up-to-date.</p>
              <p>6.2 The Client agrees to notify the Company immediately of any changes to submitted documentation.</p>
              <p>6.3 The Company reserves the right to request additional documentation at any time.</p>

              <h3 className="text-lg font-bold text-navy">7. DEFAULT AND PENALTIES</h3>
              <p>7.1 Upon default of any payment, the full outstanding balance becomes immediately due and payable.</p>
              <p>7.2 A default penalty of 10% of the outstanding amount will be levied.</p>
              <p>7.3 All collection costs, including legal fees, collection commission, and tracing fees, shall be for the Client's account.</p>
              <p>7.4 The Company reserves the right to report defaults to the credit bureaus in accordance with the NCA.</p>

              <h3 className="text-lg font-bold text-navy">8. DISPUTE RESOLUTION</h3>
              <p>8.1 Any dispute arising out of or in connection with this Agreement shall first be submitted to mediation in accordance with the rules of the Arbitration Foundation of South Africa (AFSA).</p>
              <p>8.2 Should mediation fail, the dispute shall be resolved by arbitration in Johannesburg in accordance with AFSA rules.</p>
              <p>8.3 The Client has the right to lodge complaints with the National Credit Regulator or the Credit Ombud.</p>

              <h3 className="text-lg font-bold text-navy">9. PRIVACY AND DATA PROTECTION</h3>
              <p>9.1 The Company processes personal information in accordance with the Protection of Personal Information Act 4 of 2013 ("POPIA").</p>
              <p>9.2 The Company will only use personal information for the purpose of providing services and credit assessment.</p>
              <p>9.3 The Client has the right to access and correct their personal information held by the Company.</p>
              <p>9.4 The Company will not share personal information with third parties without consent, except as required by law.</p>

              <h3 className="text-lg font-bold text-navy">10. GOVERNING LAW</h3>
              <p>10.1 This Agreement shall be governed by the laws of the Republic of South Africa.</p>
              <p>10.2 The parties agree to the jurisdiction of the Magistrate's Court having jurisdiction in the area where the Company is domiciled.</p>
              <p>10.3 This Agreement is subject to the National Credit Act 34 of 2005 and the Protection of Personal Information Act 4 of 2013.</p>
              
              <div className="mt-6 pt-4 border-t border-gray-200">
                <p className="text-center text-gray-500 text-xs">© 2026 One Purpose Logistics (Pty) Ltd. All rights reserved.</p>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 rounded-b-2xl flex justify-end">
              <button
                onClick={() => {
                  setAcceptedTerms(true);
                  setShowTerms(false);
                  toast.success('Terms accepted');
                }}
                className="px-6 py-2 bg-navy text-white rounded-lg hover:bg-green hover:text-navy transition-colors"
              >
                I Accept & Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientSignup;